import React, { useState, useEffect, useMemo } from 'react';
import { Box, useTheme, Typography, Button, Card, CardContent, useMediaQuery, TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { tokens } from '../../theme';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import { useNavigate, useParams } from "react-router-dom";
import useExaminationService from '../../api/services/examinationService';
import Table from '../../components/Table';
import Header from "../../components/Header";
import LoadingSpinner from '../../components/LoadingSpinner';
import { DataGrid } from '@mui/x-data-grid';
import * as image from '../../assets';

const Student_Examination = () => {
  const { classId, studentId } = useParams();
  const theme = useTheme();
  const borderColor = theme.palette.mode === 'dark' ? 'white' : 'black';
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [examinations, setExaminations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const { fetchExaminationTypesByClassId } = useExaminationService();


  useEffect(() => {
    const fetchData = async () => {
      if (classId) {
        setIsLoading(true);
        const data = await fetchExaminationTypesByClassId(classId);
        // Filter only PUBLISHED examinations
        const publishedExams = (data || []).filter(exam => exam.status === "PUBLISHED");
        const sortedData = publishedExams.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setExaminations(sortedData);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [classId]);

  // Filter examinations based on search query
  const filteredExaminations = useMemo(() => {
    if (!searchQuery.trim()) {
      return examinations;
    }
    const query = searchQuery.toLowerCase().trim();
    return examinations.filter(exam => 
      exam.examinationName?.toLowerCase().includes(query) ||
      exam.examMarks?.toString().includes(query)
    );
  }, [examinations, searchQuery]);

  // Handle view results navigation
  const handleViewResults = (exam) => {
    const { classType, examMarks, id } = exam;
    
    if (classType === "O-LEVEL" && examMarks === "100") {
      navigate(`/student_results_marks/${studentId}/${classId}/${id}`);
    } else if (classType === "O-LEVEL") {
      navigate(`/student_results/${studentId}/${classId}/${id}`);
    } else if (classType === "A-LEVEL" && examMarks === "100") {
      navigate(`/alevel_student_results_marks/${studentId}/${classId}/${id}`);
    } else if (classType === "A-LEVEL") {
      navigate(`/alevel_student_results/${studentId}/${classId}/${id}`);
    }
  };

  const columns = [
    {
      field: 'examinationName',
      headerName: 'Jina la mtihani',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'examMarks',
      headerName: 'alama za mtihani',
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleViewResults(params.row)}
          >
             Tazama Matokeo
          </Button>
        );
      },
    }

  ];


  return (
    <Box sx={{ ml: '20px', mr: '20px' }}>
      {/* Header */}
      <Box
        height="auto"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        border={`1px solid ${borderColor}`}
        mb={2}
        flexWrap="wrap"
      >
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
          p={1}
          justifyContent="center"
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
            <Box
              component="img"
              src={image.visigalogo}
              alt="logo"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography
              variant="h7"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
            >
              St Mary's Junior Seminary (VISIGA SEMINARY)
            </Typography>
          </Box>
        </Box>

        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
          p={2}
          textAlign="center"
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' } }}
          >
            Orodha ya majaribio na mitihani yote iliyofanywa na mwanao
          </Typography>
        </Box>

        <Box
          flex={1}
          display="flex"
          alignItems="center"
          p={1}
          justifyContent="center"
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
            <Box
              component="img"
              src={image.cathedral5}
              alt="arch"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
            >
              Seminary under Archdiocese of Dar es Salaam
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Examination Table */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" align="start">
            ORODHA YA MITIHANI
          </Typography>
          
          {/* Search Field */}
          <TextField
            placeholder="Tafuta kwa jina la mtihani..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              minWidth: { xs: '100%', sm: '300px' },
              maxWidth: { xs: '100%', sm: '400px' },
            }}
          />
        </Box>

        {isLoading ? (
          <LoadingSpinner message="Loading examinations..." height="200px" />
        ) : filteredExaminations.length > 0 ? (
          <>
            {isSmallScreen ? (
              // Card view for small screens
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredExaminations.map((exam) => (
                  <Card
                    key={exam.id}
                    sx={{
                      border: `1px solid ${borderColor}`,
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                  >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Examination Name and Exam Marks in one row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                              {exam.examinationName || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Marks:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}
                              >
                                {exam.examMarks || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Action Button */}
                        <Box sx={{ mt: 0.5 }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            fullWidth
                            onClick={() => handleViewResults(exam)}
                            sx={{ fontSize: '0.75rem', py: 0.5, minHeight: '32px' }}
                          >
                            Tazama Matokeo
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              // DataGrid for larger screens
              <Box sx={{ height: 500, width: '100%', mt: 1 }}>
                <DataGrid
                  rows={filteredExaminations}
                  columns={columns}
                  getRowId={(row) => row.id}
                  pageSize={5}
                  rowsPerPageOptions={[5, 10, 20]}
                  sx={{
                    '& .MuiDataGrid-cell:hover': {
                      cursor: 'default',
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary">
              {searchQuery.trim() 
                ? 'No examinations found matching your search.' 
                : 'No published examination types available.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Student_Examination;
